import { useEffect, useState } from "react";
import { fetchLatestRelease, type ReleaseManifest } from "../lib/release";

export type ReleaseStatus = "loading" | "ready" | "unavailable" | "error";

export interface ReleaseState {
  status: ReleaseStatus;
  manifest: ReleaseManifest | null;
  error: string | null;
}

// Fetches latest.json once on mount. Aborts in-flight request on unmount.
//
// States:
//   loading     — initial mount, fetch in flight
//   ready       — manifest loaded with full data
//   unavailable — 404 OR placeholder shape (null version); show "coming soon"
//   error       — network/parse error; show disabled CTA + small label
export function useRelease(): ReleaseState {
  const [state, setState] = useState<ReleaseState>({
    status: "loading",
    manifest: null,
    error: null,
  });

  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;
    fetchLatestRelease(ctrl.signal)
      .then((manifest) => {
        if (cancelled) return;
        if (manifest) {
          setState({ status: "ready", manifest, error: null });
        } else {
          setState({ status: "unavailable", manifest: null, error: null });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof Error && err.name === "AbortError") return;
        const msg = err instanceof Error ? err.message : String(err);
        setState({ status: "error", manifest: null, error: msg });
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, []);

  return state;
}
